import * as Sentry from '@sentry/nuxt';
import {storeToRefs} from 'pinia';
import {useAuth, useMaltEnvironnement, useMaltVisitorId, usePinia, useRoute, useRouter, useRuntimeConfig} from '#imports';
import {sentryOptions} from '../../common/sentry.options';
import type {MaltSentryEnvironment} from '../../types/environnement.types';

export function useSentryClientPluginSetup() {
    // This plugin is excluded from the "configDir" folder from the official Sentry plugin to avoid the dynamic import of the SDK into a second _nuxt chunk.
    // This second Nuxt chunk was creating an error in MF apps as because of the timestamp queryparam that was added to the URL (to force cache busting), this script was loaded twice.
    const runtimeConfig = useRuntimeConfig();
    const {
        sentry: {buildCommitHash, dsn, excludedRouteNames = []},
        application,
        isMF,
    } = runtimeConfig.public;

    if (dsn) {
        const route = !isMF ? useRoute() : undefined;
        const currentRouteName = route?.name?.toString();
        const isSentryEnabledForCurrentRoute = currentRouteName ? !excludedRouteNames.includes(currentRouteName) : true;
        const maltVisitorId = useMaltVisitorId();

        const maltEnvironment = useMaltEnvironnement();
        let sentryEnvironment: MaltSentryEnvironment = 'local';

        if (['DEV', 'DEV_INTEGRATION'].includes(maltEnvironment)) {
            sentryEnvironment = 'local';
        } else if (maltEnvironment === 'INTEGRATION') {
            sentryEnvironment = 'integration';
        } else if (maltEnvironment === 'PRODUCTION') {
            sentryEnvironment = 'production';
        } else {
            sentryEnvironment = 'local';
        }

        const authStore = useAuth();
        const {user} = storeToRefs(authStore);
        const router = !isMF ? useRouter() : undefined;

        Sentry.init({
            dsn,
            dist: application,
            environment: sentryEnvironment || undefined,
            enabled: isSentryEnabledForCurrentRoute,
            integrations: [
                Sentry.captureConsoleIntegration({levels: ['error']}),
                ...(router
                    ? [
                          Sentry.browserTracingIntegration({
                              router,
                          }),
                      ]
                    : []),
                Sentry.extraErrorDataIntegration(),
                Sentry.piniaIntegration(usePinia()),
                Sentry.vueIntegration({
                    tracingOptions: {
                        trackComponents: true,
                    },
                }),
            ],
            ...sentryOptions,
            beforeSend(event, hint) {
                if (!isSentryEnabledForCurrentRoute) {
                    return null;
                }

                const selectedIdentity = user.value?.identities?.find((identity) => identity.selected);
                if (selectedIdentity && user.value) {
                    event.user = {
                        id: user.value.id,
                        accountId: user.value.id,
                        identityId: selectedIdentity.id,
                        email: user.value.email,
                        visitorId: maltVisitorId.value,
                    };
                } else if (maltVisitorId.value) {
                    // Sentry displays user info from `id`; anonymous users only have maltVisitorId.
                    event.user = {
                        id: maltVisitorId.value,
                        visitorId: maltVisitorId.value,
                    };
                }

                return sentryOptions.beforeSend(event, hint);
            },
        });

        Sentry.setTags({application, buildCommitHash, layer: 'client'});

        Sentry.setContext('Build', {
            commitHash: buildCommitHash,
            shortCommitHash: buildCommitHash?.slice(0, 7),
        });
    }
}
